import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Link, Collapse, useCollapseItem, Typography, Button } from '@vtblife/uikit';

import { cookieApiService } from '../../../services/cookie-api';
import { reportErrorToSentry } from '../../utils';
import { isAutotestUserAgent } from '../../../utils';

const sendAnalyticsEvent = (eventAction: 'show' | 'open_more' | 'close_more' | 'reject' | 'accept') => {
    window?.dataLayer.push({
        event: 'banner-cookies',
        eventCategory: 'cookies',
        eventAction,
        eventLabel: '',
    });
};

const CookieConsentHeaderReactive = () => {
    const { opened } = useCollapseItem();
    return <Typography variant="primary">{opened ? 'Скрыть' : 'Подробнее'}</Typography>;
};

export const CookieConsentModal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const acceptCookies = useCallback(() => {
        cookieApiService
            .setCookieConsent()
            .catch((error) => {
                reportErrorToSentry({
                    error,
                    extra: { when: 'Failed to set cookie consent' },
                    level: 'warning',
                });
            })
            .finally(() => {
                window.__hideCookiesModal();
                sendAnalyticsEvent('accept');
                window.location.reload();
            });
    }, []);

    const rejectCookies = useCallback(() => {
        cookieApiService
            .setCookieConsent('rejected')
            .catch((error) => {
                reportErrorToSentry({
                    error,
                    extra: { when: 'Failed to set cookie consent' },
                    level: 'warning',
                });
            })
            .finally(() => {
                window.__hideCookiesModal();
                sendAnalyticsEvent('reject');
                setIsModalOpen(false);
            });
    }, []);

    const onCollapseToggle = useCallback((isOpened: boolean) => {
        sendAnalyticsEvent(isOpened ? 'open_more' : 'close_more');
    }, []);

    useEffect(() => {
        if (typeof navigator !== 'undefined') {
            const isTest = isAutotestUserAgent(window.navigator.userAgent);
            if (window.__isCookieModalVisible && !isTest) {
                setIsModalOpen(true);
                sendAnalyticsEvent('show');
            }
        }
    }, []);

    return (
        <Modal size="m" opened={isModalOpen} closable={false}>
            <div className="cookie-consent-content-modal">
                <Modal.Title>Мы&nbsp;используем файлы cookie и&nbsp;сервисы веб&#8209;аналитики</Modal.Title>
                Нажимая &laquo;Принять все&raquo; вы&nbsp;даёте{' '}
                <Link href="https://cdn.m2.ru/assets/pdfs/personal-data-agreement-cookie.pdf" target="blank">
                    согласие на&nbsp;обработку cookie-файлов
                </Link>{' '}
                (подробнее в&nbsp;
                <Link href="https://cdn.m2.ru/assets/pdfs/privacy-policy.pdf" target="blank">
                    Политике
                </Link>
                ). М2&nbsp;обрабатывает cookie, в&nbsp;том числе через сервисы веб-аналитики Яндекс Метрика
                и&nbsp;MyTracker с&nbsp;целью обеспечения функционирования сайта, аналитики действий на&nbsp;сайте
                и&nbsp;улучшения качества обслуживания.
                <div className="cookie-consent-collapse-modal">
                    <Collapse transparent>
                        <Collapse.Item onToggle={onCollapseToggle}>
                            <Collapse.Title>
                                <CookieConsentHeaderReactive />
                                <Collapse.TitleAddon></Collapse.TitleAddon>
                            </Collapse.Title>
                            <Typography>
                                Нажимая &laquo;Отклонить все&raquo; или продолжая использовать сайт, М2&nbsp;будет
                                обрабатывать только функциональные cookie-файлы, необходимые для работы сайта. Чтобы
                                отказаться от&nbsp;использования cookie-файлов (в&nbsp;том числе функциональных), вам
                                следует прекратить использовать сайт.
                            </Typography>
                        </Collapse.Item>
                    </Collapse>
                </div>
                <Modal.Actions>
                    <Button dataTest="accept-cookie-button" fullWidth onClick={acceptCookies} size="m">
                        Принять все
                    </Button>
                    <Button
                        variant="secondary"
                        dataTest="reject-cookie-button"
                        fullWidth
                        onClick={rejectCookies}
                        size="m"
                    >
                        Отклонить все
                    </Button>
                </Modal.Actions>
            </div>
        </Modal>
    );
};
